import { Field, Formik } from 'formik'
import moment from 'moment'
import { useMemo, useState } from 'react'

import { Box, Grid, GridItem, HStack, Text, VStack } from '@chakra-ui/react'
import { useStore } from '@tanstack/react-store'
import { Link } from 'react-router-dom'
import { userIdentification } from 'shared/constants/user'
import { userCardStore } from 'shared/store/userCard'
import { getDateReadFormat } from 'shared/utils/dateFormatting'
import { Button, Heading, InputForm, SelectForm, TextAreaForm } from 'uikit'
import { toaster } from 'uikit/Toaster'
import { Form, FormGrid, FormSection } from 'uikit/layout'
import {
  errorCode,
  formFields,
  formFieldsLabel,
  passportCountry,
  passportCountryOptions,
} from './UserEditForm.constants'
import { getPassportSeriesAndNumber } from './UserEditForm.utils'
import { validationSchema } from './UserEditForm.validation'
import { useUpdateUser } from './services'
import { useSyncUser } from './services/useSyncUser'

export const UserEditForm = () => {
  const userCard = useStore(userCardStore)
  const passportData = getPassportSeriesAndNumber(userCard.passport, userCard.identityCountry)
  const [currentErrorInfo, setCurrentErrorInfo] = useState()
  const isDuplicate = currentErrorInfo?.error === errorCode.DUPLICATE
  const addressFields = userCard?.registrationAddress?.addressFields

  const { refetch: syncUserData, isLoading: isSyncingUser } = useSyncUser(userCard.id)
  const { mutate: sendUserData, isLoading: userDataIsMutate } = useUpdateUser()

  const fieldsIsDisabled = userDataIsMutate

  const handleForm = async (values) => {
    const {
      series,
      number,
      country,
      regionName,
      regionType,
      districtName,
      settlementName,
      settlementType,
      streetName,
      streetType,
      houseNumber,
      block,
      unitNumber,
      email,
      ...rest
    } = values

    const forceValues = isDuplicate ? { force: true } : undefined

    sendUserData(
      {
        id: userCard.id,
        passport: series + number,
        registrationAddress: {
          addressFields: {
            country,
            regionName,
            regionType,
            districtName,
            settlementName,
            settlementType,
            streetName,
            streetType,
            houseNumber,
            block,
            unitNumber,
          },
        },
        email: email ? email.trim() : email,
        ...forceValues,
        ...rest,
      },
      {
        onSuccess: () => {
          setCurrentErrorInfo(undefined)
        },
        onError: (err) => {
          const data = err.response.data
          const message = data.message || err.message

          setCurrentErrorInfo(data)

          if (data.error === errorCode.DUPLICATE) {
            toaster.create({
              type: 'warning',
              title: 'Пользователь с таким номером уже существует',
              description: message,
            })
          } else {
            toaster.create({
              type: 'error',
              title: 'Произошла ошибка при обновлении данных пользователя',
              description: message,
            })
          }
        },
      }
    )
  }

  const initialValues = useMemo(
    () => ({
      series: passportData?.series || undefined,
      number: passportData?.number || undefined,
      dateIssue: userCard.dateIssue ? moment(userCard.dateIssue).format('YYYY-MM-DD') : undefined,
      issueOrgCode: userCard.issueOrgCode || undefined,
      issue: userCard.issue || undefined,
      identityCountry: userCard.identityCountry,
      phone: userCard.phone || undefined,
      email: userCard.email || undefined,
      firstName: userCard.firstName || undefined,
      lastName: userCard.lastName || undefined,
      middleName: userCard.middleName || undefined,
      country: addressFields?.country || undefined,
      regionName: addressFields?.regionName || undefined,
      regionType: addressFields?.regionType || undefined,
      districtName: addressFields?.districtName || undefined,
      settlementName: addressFields?.settlementName || undefined,
      settlementType: addressFields?.settlementType || undefined,
      streetName: addressFields?.streetName || undefined,
      streetType: addressFields?.streetType || undefined,
      houseNumber: addressFields?.houseNumber || undefined,
      block: addressFields?.block || undefined,
      unitNumber: addressFields?.unitNumber || undefined,
    }),
    [userCard]
  )

  const userSyncIsDisavbled = ![userIdentification.FULL, userIdentification.FULL_DOSSIER].includes(
    userCard.identification
  )

  return (
    <VStack alignItems="flex-start" maxW="1000px">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleForm}
        validationSchema={validationSchema}
      >
        {(formProps) => {
          const { values } = formProps

          return (
            <Form
              w="100%"
              onSubmit={formProps.handleSubmit}
              footer={
                <HStack gap={3} ml="auto">
                  <Button
                    colorPalette="orange"
                    loading={isSyncingUser}
                    onClick={() => syncUserData()}
                    disabled={userSyncIsDisavbled}
                  >
                    Синхронизировать данные с СКБ
                  </Button>

                  <Button
                    type="submit"
                    colorPalette="orange"
                    loading={userDataIsMutate || isSyncingUser}
                    disabled={!formProps.isValid || isDuplicate}
                  >
                    Обновить
                  </Button>
                </HStack>
              }
            >
              <Heading mb={4}>ФИО</Heading>
              <FormGrid columns={2} alignItems="none" mb={6}>
                <Field
                  name={formFields.firstName}
                  label={formFieldsLabel[formFields.firstName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.lastName}
                  label={formFieldsLabel[formFields.lastName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.middleName}
                  label={formFieldsLabel[formFields.middleName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
              </FormGrid>

              {!!userCard?.ekengData && (
                <Grid color="gray.500" maxW="250px" mb={8}>
                  <Heading mb={2}>ekeng</Heading>

                  <Grid templateColumns="100px 300px">
                    <InfoFieldForm value={userCard?.ekengData?.englishFirstName} label="Имя" />
                    <InfoFieldForm value={userCard?.ekengData?.englishLastName} label="Фамилия" />
                  </Grid>
                </Grid>
              )}

              <Heading mb={4}>Паспортные данные</Heading>
              <FormGrid columns={2} alignItems="none">
                <VStack>
                  <Field
                    name={formFields.identityCountry}
                    label={formFieldsLabel[formFields.identityCountry]}
                    component={SelectForm}
                    placeholder="Укажите страну"
                    options={passportCountryOptions}
                    isDisabled
                  />
                  {values.identityCountry === passportCountry.ARM && (
                    <Text color="orange">
                      Внимание! Для паспорта РА серией являются две первые буквы, номером -
                      оставшиеся семь цифр. Организация, выдавшая документ должна содержать
                      трехзначный код.
                    </Text>
                  )}
                </VStack>
                <FormSection>
                  <FormGrid columns={2}>
                    <Field
                      name={formFields.series}
                      label={formFieldsLabel[formFields.series]}
                      component={InputForm}
                      disabled={fieldsIsDisabled}
                    />
                    <Field
                      name={formFields.number}
                      label={formFieldsLabel[formFields.number]}
                      component={InputForm}
                      disabled={fieldsIsDisabled}
                    />
                  </FormGrid>
                  <FormGrid columns={2}>
                    <Field
                      name={formFields.dateIssue}
                      label={formFieldsLabel[formFields.dateIssue]}
                      component={InputForm}
                      placeholder="ГГГГ-ММ-ДД"
                      disabled={fieldsIsDisabled}
                    />
                    {values.identityCountry === passportCountry.RU && (
                      <Field
                        name={formFields.issueOrgCode}
                        label={formFieldsLabel[formFields.issueOrgCode]}
                        component={InputForm}
                        placeholder="XXX-XXX"
                        disabled={fieldsIsDisabled}
                      />
                    )}
                  </FormGrid>
                  <Field
                    name={formFields.issue}
                    label={formFieldsLabel[formFields.issue]}
                    component={TextAreaForm}
                    disabled={fieldsIsDisabled}
                  />
                </FormSection>
              </FormGrid>

              {!!userCard?.ekengData && (
                <Box color="gray.500" maxW="550px" mb={8}>
                  <Heading mb={2}>ekeng</Heading>

                  <Grid templateColumns="250px 300px">
                    <InfoFieldForm
                      label="Серия/Номер"
                      value={userCard?.ekengData?.documentNumber}
                    />
                    <InfoFieldForm
                      value={moment(userCard?.ekengData?.issuanceDate).format('YYYY-MM-DD')}
                      label="Дата выдачи"
                    />
                    <InfoFieldForm
                      value={moment(userCard?.ekengData?.validityDate).format('YYYY-MM-DD')}
                      label="Дата окончания"
                    />
                    <InfoFieldForm
                      value={userCard?.ekengData?.documentDepartment}
                      label="Орган выдающий документ"
                    />
                    <InfoFieldForm
                      value={getDateReadFormat(userCard?.ekengData?.birthDate)}
                      label="Дата рождения"
                    />
                    <InfoFieldForm
                      value={
                        userCard?.ekengData?.isDead === undefined
                          ? undefined
                          : userCard?.ekengData?.isDead
                            ? 'Да'
                            : 'Нет'
                      }
                      label="Умер"
                    />
                  </Grid>
                </Box>
              )}

              <Heading mb={4}>Адрес регистрации</Heading>
              <FormGrid mb={4} columns={3} alignItems="none">
                <Field
                  name={formFields.country}
                  label={formFieldsLabel[formFields.country]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.regionName}
                  label={formFieldsLabel[formFields.regionName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.regionType}
                  label={formFieldsLabel[formFields.regionType]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.districtName}
                  label={formFieldsLabel[formFields.districtName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.settlementName}
                  label={formFieldsLabel[formFields.settlementName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.settlementType}
                  label={formFieldsLabel[formFields.settlementType]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.streetName}
                  label={formFieldsLabel[formFields.streetName]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.streetType}
                  label={formFieldsLabel[formFields.streetType]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.houseNumber}
                  label={formFieldsLabel[formFields.houseNumber]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.block}
                  label={formFieldsLabel[formFields.block]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.unitNumber}
                  label={formFieldsLabel[formFields.unitNumber]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
              </FormGrid>

              {!!userCard?.ekengData && (
                <Box mb={8} color="gray.500">
                  <Heading mb={2}>ekeng</Heading>
                  <Grid templateColumns="150px 300px">
                    <InfoFieldForm value={userCard?.ekengData?.street} label="Улица" />
                    <InfoFieldForm value={userCard?.ekengData?.building} label="Здание" />
                    <InfoFieldForm value={userCard?.ekengData?.apartment} label="Квартира" />
                    <InfoFieldForm value={userCard?.ekengData?.community} label="Община" />
                    <InfoFieldForm value={userCard?.ekengData?.region} label="Регион" />
                  </Grid>
                </Box>
              )}

              <Heading mb={4}>Контакты</Heading>
              <FormGrid columns={2} alignItems="none">
                <Field
                  name={formFields.phone}
                  label={formFieldsLabel[formFields.phone]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
                <Field
                  name={formFields.email}
                  label={formFieldsLabel[formFields.email]}
                  component={InputForm}
                  disabled={fieldsIsDisabled}
                />
              </FormGrid>
              {isDuplicate && (
                <Box>
                  <HStack mt="10px" gap="5px">
                    <Text color="red.400">Такой номер или email уже существует у пользователя</Text>
                    <Text color={'blue.300'}>
                      <Link to={`./${currentErrorInfo?.data.id}`} target="_blank">
                        {currentErrorInfo?.data.id}
                      </Link>
                    </Text>
                  </HStack>
                  <HStack mt="10px">
                    <Text>Заблокировать дубликат?</Text>
                    <Button
                      size="sm"
                      colorPalette="orange"
                      onClick={() => formProps.handleSubmit()}
                      minW="60px"
                    >
                      Да
                    </Button>
                    <Button size="sm" onClick={() => setCurrentErrorInfo(undefined)}>
                      Отмена
                    </Button>
                  </HStack>
                  c
                </Box>
              )}
            </Form>
          )
        }}
      </Formik>
    </VStack>
  )
}

export const InfoFieldForm = ({ value, label }) => {
  return (
    <>
      <GridItem>
        <Text fontWeight="bold">{label}</Text>
      </GridItem>
      <GridItem>
        <Text>{value || ' – '}</Text>
      </GridItem>
    </>
  )
}
